import type { PluginEngineLoginUser } from '~/types/blog'
import {
  clearPluginAuthSession,
  getPluginAuthExpiresAt,
  getPluginAuthToken,
  getPluginAuthUser,
  hasValidPluginAuthSession,
  PLUGIN_AUTH_CHANGED_EVENT,
  setPluginAuthSession
} from '~/utils/pluginAuth'

type PluginAuthState = {
  initialized: boolean
  authenticated: boolean
  user: PluginEngineLoginUser | null
}

// usePluginAuth 管理博客前台的 Engine 插件互联会话。
export const usePluginAuth = () => {
  const blogApi = useBlogApi()
  const state = useState<PluginAuthState>('plugin-auth-state', () => ({
    initialized: false,
    authenticated: false,
    user: null
  }))
  const loginLoading = useState('plugin-auth-login-loading', () => false)
  const logoutLoading = useState('plugin-auth-logout-loading', () => false)
  const errorMessage = useState('plugin-auth-error', () => '')
  let expiryTimer: ReturnType<typeof setTimeout> | null = null

  const isAuthenticated = computed(() => state.value.initialized && state.value.authenticated)
  const userName = computed(() => String(
    state.value.user?.nickname || state.value.user?.username || '用户'
  ).trim())

  // refreshFromStorage 从本地会话同步响应式登录状态。
  const refreshFromStorage = () => {
    if (expiryTimer) {
      clearTimeout(expiryTimer)
      expiryTimer = null
    }
    const user = getPluginAuthUser()
    const authenticated = hasValidPluginAuthSession() && user !== null
    state.value = {
      initialized: true,
      authenticated,
      user: authenticated ? user : null
    }
    if (authenticated) {
      const expiresIn = getPluginAuthExpiresAt() - Date.now()
      const refreshDelay = Math.min(Math.max(expiresIn + 50, 50), 2_147_483_647)
      expiryTimer = setTimeout(refreshFromStorage, refreshDelay)
    }
  }

  // verifyStoredToken 向 Engine 校验本地 JWT，并以令牌解析结果刷新会话信息。
  const verifyStoredToken = async () => {
    const storedToken = getPluginAuthToken()
    if (!storedToken) {
      refreshFromStorage()
      return false
    }
    try {
      const data = await blogApi.verifyPluginEngineToken()
      const token = getPluginAuthToken() || storedToken
      if (!data?.user || !data.expiresAt) {
        clearPluginAuthSession()
        return false
      }
      setPluginAuthSession(token, data.expiresAt, data.user)
      return true
    } catch {
      return false
    } finally {
      refreshFromStorage()
    }
  }

  // handlePageVisible 在页面重新激活时立即复核会话有效期。
  const handlePageVisible = () => {
    if (document.visibilityState === 'visible') {
      refreshFromStorage()
    }
  }

  // startLogin 请求 Engine 登录地址并跳转。
  const startLogin = async () => {
    if (!import.meta.client || loginLoading.value) {
      return
    }
    loginLoading.value = true
    errorMessage.value = ''
    try {
      const data = await blogApi.getPluginEngineInfo(window.location.href)
      const loginURL = String(data.engine.loginUrl || '').trim()
      if (!/^https?:\/\//i.test(loginURL)) {
        throw new Error('Engine 登录入口暂不可用')
      }
      window.location.href = loginURL
    } catch (error) {
      errorMessage.value = (error as Error)?.message || 'Engine 登录入口暂不可用'
    } finally {
      loginLoading.value = false
    }
  }

  // bootstrapFromURL 兑换 Engine 登录重定向携带的一次性票据。
  const bootstrapFromURL = async () => {
    if (!import.meta.client) {
      return
    }
    const url = new URL(window.location.href)
    const code = String(url.searchParams.get('code') || '').trim()
    if (!code) {
      await verifyStoredToken()
      return
    }

    url.searchParams.delete('code')
    window.history.replaceState({}, document.title, url.toString())
    errorMessage.value = ''
    try {
      const data = await blogApi.exchangePluginEngineLogin(code)
      const token = String(data.token || '').trim()
      if (!token || !data.expiresAt || !data.user) {
        throw new Error('Engine 登录票据兑换失败')
      }
      setPluginAuthSession(token, data.expiresAt, data.user)
    } catch (error) {
      errorMessage.value = (error as Error)?.message || 'Engine 登录票据兑换失败'
    } finally {
      await verifyStoredToken()
    }
  }

  // logout 注销当前 Engine 会话并清理本地状态。
  const logout = async () => {
    if (logoutLoading.value) {
      return
    }
    logoutLoading.value = true
    errorMessage.value = ''
    try {
      if (hasValidPluginAuthSession()) {
        await blogApi.logoutPluginEngine()
      }
    } catch (error) {
      errorMessage.value = (error as Error)?.message || '退出登录失败'
    } finally {
      clearPluginAuthSession()
      refreshFromStorage()
      logoutLoading.value = false
    }
  }

  onMounted(() => {
    window.addEventListener(PLUGIN_AUTH_CHANGED_EVENT, refreshFromStorage)
    document.addEventListener('visibilitychange', handlePageVisible)
    refreshFromStorage()
  })

  onBeforeUnmount(() => {
    window.removeEventListener(PLUGIN_AUTH_CHANGED_EVENT, refreshFromStorage)
    document.removeEventListener('visibilitychange', handlePageVisible)
    if (expiryTimer) {
      clearTimeout(expiryTimer)
      expiryTimer = null
    }
  })

  return {
    user: computed(() => state.value.user),
    userName,
    isAuthenticated,
    loginLoading: readonly(loginLoading),
    logoutLoading: readonly(logoutLoading),
    errorMessage: readonly(errorMessage),
    refreshFromStorage,
    verifyStoredToken,
    startLogin,
    bootstrapFromURL,
    logout
  }
}
